import { useEffect, useRef, useState } from "react";
import {
  DEFAULT_ACTUAL, DEFAULT_SIMPLE,
  type AxisRates, type FlightAxis, type RatesActual, type RatesSimple,
} from "../fpvRates";

export type FpvParams = {
  rateMode: "simple" | "actual";
  rates: RatesSimple;   // simple mode: max deg/s per axis
  actual: RatesActual;  // Betaflight Actual Rates per axis
  thrust: number;  // max thrust accel (scene units/s^2)
  gravity: number; // downward accel (scene units/s^2); hover throttle ≈ gravity/thrust
  tilt: number;    // FPV camera up-tilt, deg (90 = straight up — race-cam territory)
  drag: number;    // linear drag coefficient
  fisheye: number; // lens amount 0..1 (0 = rectilinear, 1 = full GoPro)
  ground: boolean;   // grass floor visible (touching it respawns the pilot)
  groundAlt: number; // how far the animation centre sits above ground, metres
  groundDark: number; // 0 = full bright grass, 1 = lights-out
  streaks: boolean;  // red edge streaks when a show drone gets close
  warnDist: number;  // proximity warning radius, metres
  worldScale: number; // how big the world FEELS (0.1–10×): translational kinematics ÷ scale
};

// Free's tuned flight defaults (2026-07-24): floatier than the originals —
// thrust 12 / gravity 4.5 / drag 0.3, a proper 30° race tilt, lens flat.
export const DEFAULT_FPV: FpvParams = {
  rateMode: "simple",
  rates: DEFAULT_SIMPLE,
  actual: DEFAULT_ACTUAL,
  thrust: 12, gravity: 4.5, tilt: 30, drag: 0.3,
  fisheye: 0,
  ground: true, groundAlt: 12, groundDark: 0.35,
  streaks: true, warnDist: 3,
  worldScale: 1,
};

// v2: the 2026-07-24 defaults round — a stale v1 save would shadow the new
// tuning, so the key bumps and everyone starts fresh from these.
const LS = "swarmsim_fpv_v2";

// merge saved params over defaults, surviving schema growth (new fields keep
// their defaults; nested rate objects merge per-axis so old saves stay valid)
function loadParams(): FpvParams {
  try {
    const s = localStorage.getItem(LS);
    if (s) {
      const j = JSON.parse(s) as Partial<FpvParams>;
      return {
        ...DEFAULT_FPV,
        ...j,
        rates: { ...DEFAULT_SIMPLE, ...j.rates },
        actual: {
          roll: { ...DEFAULT_ACTUAL.roll, ...j.actual?.roll },
          pitch: { ...DEFAULT_ACTUAL.pitch, ...j.actual?.pitch },
          yaw: { ...DEFAULT_ACTUAL.yaw, ...j.actual?.yaw },
        },
      };
    }
  } catch { /* ignore */ }
  return DEFAULT_FPV;
}

export type Telemetry = {
  throttle: number; speed: number; alt: number; armed: boolean;
  crash: number;                       // performance.now() of the last ground strike (0 = never)
  prox: [number, number, number, number]; // left/right/top/bottom streak intensity 0..1
};

/**
 * Owns FPV mode: whether it's active, the flight tuning (persisted — pilots
 * hate re-entering rates), live gamepad-connected status, and a telemetry ref
 * the rig writes and the HUD reads (kept in a ref so 60 fps updates never
 * re-render React).
 */
export function useFpv() {
  const [active, setActive] = useState(false);
  const [connected, setConnected] = useState(false);
  const [params, setParams] = useState<FpvParams>(loadParams);
  const setParam = (k: keyof FpvParams) => (v: number | boolean | string) =>
    setParams((s) => ({ ...s, [k]: v }));
  const setSimpleRate = (axis: FlightAxis) => (v: number) =>
    setParams((s) => ({ ...s, rates: { ...s.rates, [axis]: v } }));
  const setActualRate = (axis: FlightAxis, k: keyof AxisRates) => (v: number) =>
    setParams((s) => ({ ...s, actual: { ...s.actual, [axis]: { ...s.actual[axis], [k]: v } } }));
  const telemetry = useRef<Telemetry>({
    throttle: 0, speed: 0, alt: 0, armed: false, crash: 0, prox: [0, 0, 0, 0],
  });

  useEffect(() => {
    try { localStorage.setItem(LS, JSON.stringify(params)); } catch { /* ignore */ }
  }, [params]);

  useEffect(() => {
    const sync = () => setConnected(Array.from(navigator.getGamepads?.() ?? []).some((g) => !!g));
    window.addEventListener("gamepadconnected", sync);
    window.addEventListener("gamepaddisconnected", sync);
    sync();
    return () => {
      window.removeEventListener("gamepadconnected", sync);
      window.removeEventListener("gamepaddisconnected", sync);
    };
  }, []);

  return { active, setActive, connected, params, setParam, setSimpleRate, setActualRate, telemetry };
}
